/**
 * The methods dbt's node selection knows (#1292), the one list the web, the editor and the runner
 * read. A selector term is `method:value`: `tag:nightly`, `path:models/staging`,
 * `config.materialized:table`. A term with no colon is a model name, a path or a glob, which dbt
 * resolves itself and this list does not judge.
 *
 * From dbt's documented "Node selection syntax" (the `MethodName` set of dbt-core 1.10). A method
 * dbt adds later is refused here until it is added to this list, which is the cheaper mistake
 * than letting a typed word through to fail a nightly run.
 */
export const DBT_SELECTOR_METHODS = [
  "fqn",
  "tag",
  "path",
  "file",
  "package",
  "config",
  "source",
  "resource_type",
  "test_type",
  "test_name",
  "state",
  "exposure",
  "metric",
  "semantic_model",
  "saved_query",
  "unit_test",
  "result",
  "source_status",
  "access",
  "group",
  "version",
  "selector",
] as const;

const KNOWN = new Set<string>(DBT_SELECTOR_METHODS);

/** `@`, a leading `+` or `N+`, a trailing `+` or `+N`: dbt's graph operators, not part of a term. */
function withoutGraphOperators(term: string): string {
  let value = term;
  if (value.startsWith("@")) value = value.slice(1);
  else value = value.replace(/^\d*\+/, "");
  return value.replace(/\+\d*$/, "");
}

/**
 * The first method word in `selector` that dbt does not have, as typed, or null when every term
 * is a known method or carries none. A space is a union and a comma an intersection, so both
 * split terms. A config key (`config.materialized`) is the method `config`.
 */
export function unknownSelectorMethod(selector: string): string | null {
  for (const raw of selector.split(/[\s,]+/)) {
    if (raw === "") continue;
    const term = withoutGraphOperators(raw);
    const colon = term.indexOf(":");
    if (colon < 0) continue;
    const word = term.slice(0, colon);
    const method = word.split(".")[0] ?? "";
    if (!KNOWN.has(method)) return word === "" ? term : word;
  }
  return null;
}

/** What a person reads when the word is not a method. The owner's sentence on #1292. */
export function unknownSelectorMethodSentence(word: string): string {
  return `"${word}" is not a dbt selector method. Try tag:, path: or a model name.`;
}
